'use client';

import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { ZoomIn, ZoomOut, Maximize2, Check } from 'lucide-react';
import { Button } from '@/components/ui/forms/button';
import { getPdfMarginInForTemplate } from '@/lib/resume/export/pdf-layout';
import { computePageOffsets } from '@/lib/resume/templates/pagination';
import {
  attachInlinePreviewEditing,
  type InlineDateTarget,
  type InlineEditTarget,
} from './attachInlinePreviewEditing';

const ZOOM_LEVELS = [0.5, 0.75, 0.9, 1.0, 1.25, 1.5];

/** How long the sheet takes to re-scale. Kept in sync with the scroll easing. */
const ZOOM_TRANSITION_MS = 220;

/** Honour the OS setting; motion here is decoration, not information. */
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
const FIT_TO_VIEW_INDEX = -1;

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const MM_TO_PX = 3.7795;
const A4_WIDTH_PX = A4_WIDTH_MM * MM_TO_PX;
const A4_HEIGHT_PX = A4_HEIGHT_MM * MM_TO_PX;
const PREVIEW_PAGE_GAP_PX = 20;
/** A rendered page window never collapses below this height. */
const MIN_PAGE_WINDOW_PX = 16;

// Layout-only. The page-break policy is NOT restated here: it ships with the
// template (see lib/resume/templates/pagination.ts) and is read back off the
// DOM below, so the preview and the PDF export obey one set of rules.
const PREVIEW_PRINT_OVERRIDES = `
<style id="resume-preview-print-overrides">
  html, body {
    margin: 0 !important;
    padding: 0 !important;
  }
  .resume {
    width: 100% !important;
    min-height: auto !important;
    padding: 0 !important;
    margin: 0 !important;
  }
</style>
`;

function buildPrintPreviewHtml(src: string): string {
  if (!src) return '';
  if (src.includes('resume-preview-print-overrides')) return src;
  if (/<\/head>/i.test(src)) {
    return src.replace(/<\/head>/i, `${PREVIEW_PRINT_OVERRIDES}</head>`);
  }
  return `${PREVIEW_PRINT_OVERRIDES}${src}`;
}

/**
 * The background the browser would paint across every printed page.
 *
 * In paged media the root element's background propagates to the page canvas
 * and repaints on each sheet; the document body only spans its own content.
 * The preview clips each page to its slice of that content, so without
 * replaying the canvas underneath, anything below a page break renders as bare
 * paper — which is how the focus-sidebar column came to stop mid-page.
 *
 * Values come back already resolved (custom properties substituted, lengths
 * absolute), so they can be applied outside the iframe unchanged.
 */
export interface CanvasBackground {
  backgroundColor: string;
  backgroundImage: string;
  backgroundSize: string;
  backgroundPosition: string;
  backgroundRepeat: string;
}

function isPainted(style: CSSStyleDeclaration): boolean {
  return (
    style.backgroundImage !== 'none' ||
    (style.backgroundColor !== 'transparent' && style.backgroundColor !== 'rgba(0, 0, 0, 0)')
  );
}

function readCanvasBackground(doc: Document): CanvasBackground | null {
  const view = doc.defaultView;
  if (!view || !doc.documentElement) return null;

  const rootStyle = view.getComputedStyle(doc.documentElement);
  // The root wins; body only propagates to the canvas when the root paints nothing.
  const style = isPainted(rootStyle) || !doc.body ? rootStyle : view.getComputedStyle(doc.body);
  if (!isPainted(style)) return null;

  return {
    backgroundColor: style.backgroundColor,
    backgroundImage: style.backgroundImage,
    backgroundSize: style.backgroundSize,
    backgroundPosition: style.backgroundPosition,
    backgroundRepeat: style.backgroundRepeat,
  };
}

interface PreviewPageProps {
  html: string;
  offsetPx: number;
  visibleHeightPx: number;
  pageNumber: number;
  scale: number;
  /** False when the user prefers reduced motion. */
  animateScale: boolean;
  contentHeightPx: number;
  pdfMarginMm: number;
  pdfContentWidthMm: number;
  pageInsetYMm: number;
  canvasBackground: CanvasBackground | null;
  /** When set, blocks in this page become hoverable and click-to-edit. */
  onRequestEdit?: (target: InlineEditTarget) => void;
  onRequestDateEdit?: (target: InlineDateTarget) => void;
}

function PreviewPage({
  html,
  offsetPx,
  visibleHeightPx,
  pageNumber,
  scale,
  animateScale,
  contentHeightPx,
  pdfMarginMm,
  pdfContentWidthMm,
  pageInsetYMm,
  canvasBackground,
  onRequestEdit,
  onRequestDateEdit,
}: PreviewPageProps) {
  const iframeHeightPx = Math.max(contentHeightPx, A4_HEIGHT_PX);
  const detachRef = useRef<(() => void) | null>(null);

  // Listeners go inside the iframe document, so they must be re-attached each
  // time it reloads (every content change replaces srcDoc).
  const handleFrameLoad = useCallback(
    (event: React.SyntheticEvent<HTMLIFrameElement>) => {
      detachRef.current?.();
      detachRef.current = null;
      const doc = event.currentTarget.contentDocument;
      if (doc && onRequestEdit) {
        detachRef.current = attachInlinePreviewEditing(doc, {
          onRequestEdit,
          onRequestDateEdit,
        });
      }
    },
    [onRequestEdit, onRequestDateEdit]
  );

  useEffect(() => () => detachRef.current?.(), []);
  const visibleHeightMm = visibleHeightPx / MM_TO_PX;

  return (
    <div
      style={{
        width: `calc(${A4_WIDTH_MM}mm * ${scale})`,
        height: `calc(${A4_HEIGHT_MM}mm * ${scale})`,
        position: 'relative',
        // The wrapper drives layout, so it has to ease alongside the transform
        // below — animating only the transform would leave the surrounding
        // scroll area snapping to the new size a frame ahead of the sheet.
        transition: animateScale ? `width ${ZOOM_TRANSITION_MS}ms ease, height ${ZOOM_TRANSITION_MS}ms ease` : undefined,
      }}
    >
      <div
        className="relative overflow-hidden bg-white shadow-lg ring-1 ring-gray-300"
        style={{
          width: `${A4_WIDTH_MM}mm`,
          height: `${A4_HEIGHT_MM}mm`,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          transition: animateScale ? `transform ${ZOOM_TRANSITION_MS}ms ease` : undefined,
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      >
        {canvasBackground && (
          // Full bleed, not inset by the print margin: a propagated canvas
          // background covers the entire sheet, margins included.
          <div aria-hidden className="absolute inset-0" style={canvasBackground} />
        )}

        <div
          className="absolute overflow-hidden"
          style={{
            top: `${pdfMarginMm + pageInsetYMm}mm`,
            left: `${pdfMarginMm}mm`,
            width: `${pdfContentWidthMm}mm`,
            height: `${visibleHeightMm}mm`,
          }}
        >
          <iframe
            srcDoc={html}
            title={`Resume Preview Page ${pageNumber}`}
            className={`h-full w-full border-0${onRequestEdit ? '' : ' pointer-events-none'}`}
            style={{
              height: `${iframeHeightPx}px`,
              transform: `translateY(-${offsetPx}px)`,
              transformOrigin: 'top left',
            }}
            scrolling="no"
            onLoad={handleFrameLoad}
          />
        </div>
      </div>
    </div>
  );
}

interface ResumeDocumentPreviewProps {
  html: string;
  templateId?: string;
  defaultToFit?: boolean;
  controlsFixedToViewport?: boolean;
  /**
   * Enables click-to-edit on the rendered page. Omit for read-only surfaces
   * (the shared-resume page and thumbnails), which then keep pointer events
   * off entirely.
   */
  onRequestEdit?: (target: InlineEditTarget) => void;
  onRequestDateEdit?: (target: InlineDateTarget) => void;
  /** While true the zoom bar offers Save / Cancel instead of just zoom. */
  editing?: boolean;
  onSaveEdit?: () => void;
  onCancelEdit?: () => void;
}

/** Imperative controls the editor needs: focus a field, or return to Fit. */
export interface ResumeDocumentPreviewHandle {
  /**
   * Zoom to a readable level and centre `element` in the viewport.
   *
   * Position is measured on screen rather than reconstructed from the page
   * geometry: the field lives inside an iframe that sits under a `scale()` and
   * a per-page `translateY()`, so `getBoundingClientRect()` on both the iframe
   * and the element gives the answer directly, with no margin arithmetic to
   * keep in sync with the templates.
   */
  /** Returns true when it actually changed the zoom, so the caller knows
   *  whether returning to Fit afterwards is appropriate. */
  focusField: (element: HTMLElement) => boolean;
  fitToView: () => void;
}

export function ResumeDocumentPreview({
  html,
  templateId,
  defaultToFit = false,
  controlsFixedToViewport = false,
  onRequestEdit,
  onRequestDateEdit,
  editing = false,
  onSaveEdit,
  onCancelEdit,
  previewHandleRef,
}: ResumeDocumentPreviewProps & {
  previewHandleRef?: React.MutableRefObject<ResumeDocumentPreviewHandle | null>;
}) {
  const [zoomIndex, setZoomIndex] = useState(defaultToFit ? FIT_TO_VIEW_INDEX : 2);
  const [fitScale, setFitScale] = useState(1);
  const pagesRef = useRef<HTMLDivElement | null>(null);
  // Read once: this is a user preference, not something that changes per frame.
  const [reduceMotion] = useState(prefersReducedMotion);
  const [pageOffsets, setPageOffsets] = useState<number[]>([0]);
  const [contentHeightPx, setContentHeightPx] = useState(A4_HEIGHT_PX);
  const [measuredHtml, setMeasuredHtml] = useState('');
  const [canvasBackground, setCanvasBackground] = useState<CanvasBackground | null>(null);
  const [pageInsetYPx, setPageInsetYPx] = useState(0);
  const [usableHeightPx, setUsableHeightPx] = useState(A4_HEIGHT_PX);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const measureIframeRef = useRef<HTMLIFrameElement>(null);

  const pdfMarginIn = getPdfMarginInForTemplate(templateId);
  const pdfMarginMm = pdfMarginIn * 25.4;
  const pdfMarginPx = pdfMarginIn * 96;
  const pdfContentWidthMm = A4_WIDTH_MM - pdfMarginMm * 2;
  const pdfContentHeightMm = A4_HEIGHT_MM - pdfMarginMm * 2;
  const pdfContentHeightPx = A4_HEIGHT_PX - pdfMarginPx * 2;

  const printHtml = useMemo(() => buildPrintPreviewHtml(html), [html]);

  const measurePageMetrics = useCallback(() => {
    const doc = measureIframeRef.current?.contentDocument;
    if (!doc) return null;
    return { ...computePageOffsets(doc, pdfContentHeightPx), canvasBackground: readCanvasBackground(doc) };
  }, [pdfContentHeightPx]);

  const handleMeasureLoad = useCallback(() => {
    const applyMeasuredPageCount = () => {
      const metrics = measurePageMetrics();
      if (metrics === null) return;

      setContentHeightPx((prev) => (prev === metrics.contentHeightPx ? prev : metrics.contentHeightPx));
      setPageOffsets((prev) => {
        if (
          prev.length === metrics.pageOffsets.length &&
          prev.every((value, index) => Math.abs(value - metrics.pageOffsets[index]) < 1)
        ) {
          return prev;
        }
        return metrics.pageOffsets;
      });
      setPageInsetYPx((prev) => (Math.abs(prev - metrics.pageInsetYPx) < 0.5 ? prev : metrics.pageInsetYPx));
      setUsableHeightPx((prev) => (Math.abs(prev - metrics.usableHeightPx) < 0.5 ? prev : metrics.usableHeightPx));
      setCanvasBackground((prev) => {
        const next = metrics.canvasBackground;
        if (prev === next) return prev;
        if (prev && next && (Object.keys(next) as Array<keyof CanvasBackground>).every((k) => prev[k] === next[k])) {
          return prev;
        }
        return next;
      });
      setMeasuredHtml(printHtml);
    };

    applyMeasuredPageCount();

    const fontsReady = measureIframeRef.current?.contentDocument?.fonts?.ready;
    if (fontsReady) {
      void fontsReady
        .then(() => {
          applyMeasuredPageCount();
        })
        .catch(() => undefined);
    }
  }, [measurePageMetrics, printHtml]);

  useEffect(() => {
    const calculateFitScale = () => {
      const viewport = scrollViewportRef.current;
      if (!viewport) return;

      const containerWidth = viewport.clientWidth;
      const padding = 32;
      const availableWidth = containerWidth - padding;
      const calculatedScale = (availableWidth / A4_WIDTH_PX) * 0.99;

      setFitScale(Math.min(calculatedScale, 1));
    };

    calculateFitScale();

    const resizeObserver = new ResizeObserver(calculateFitScale);
    if (scrollViewportRef.current) {
      resizeObserver.observe(scrollViewportRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const currentZoom = zoomIndex === FIT_TO_VIEW_INDEX ? 'fit' : ZOOM_LEVELS[zoomIndex];
  const canZoomIn = zoomIndex === FIT_TO_VIEW_INDEX || zoomIndex < ZOOM_LEVELS.length - 1;
  const canZoomOut = zoomIndex === FIT_TO_VIEW_INDEX || zoomIndex > 0;

  const getScaleForZoomIndex = useCallback(
    (index: number) => (index === FIT_TO_VIEW_INDEX ? fitScale : ZOOM_LEVELS[index] ?? 1),
    [fitScale]
  );

  const applyZoom = useCallback(
    (nextIndex: number) => {
      const viewport = scrollViewportRef.current;
      if (!viewport) {
        setZoomIndex(nextIndex);
        return;
      }

      const prevScale = getScaleForZoomIndex(zoomIndex);
      const centerX = (viewport.scrollLeft + viewport.clientWidth / 2) / Math.max(prevScale, 0.01);
      const centerY = (viewport.scrollTop + viewport.clientHeight / 2) / Math.max(prevScale, 0.01);

      setZoomIndex(nextIndex);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const nextScale = getScaleForZoomIndex(nextIndex);
          const nextLeft = centerX * nextScale - viewport.clientWidth / 2;
          const nextTop = centerY * nextScale - viewport.clientHeight / 2;
          viewport.scrollLeft = Math.max(0, nextLeft);
          viewport.scrollTop = Math.max(0, nextTop);
        });
      });
    },
    [getScaleForZoomIndex, zoomIndex]
  );

  const handleZoomIn = () => {
    if (canZoomIn) {
      applyZoom(zoomIndex === FIT_TO_VIEW_INDEX ? 2 : zoomIndex + 1);
    }
  };

  const handleZoomOut = () => {
    if (canZoomOut) {
      applyZoom(zoomIndex === FIT_TO_VIEW_INDEX ? 1 : zoomIndex - 1);
    }
  };

  /**
   * Horizontally centre the sheet itself.
   *
   * Deliberately measured from the pages element rather than from
   * `scrollWidth`: centring on the scroll range assumes everything scrollable
   * IS the document, and anything else that widens the container (a hidden
   * measurement frame, once) silently drags the sheet off screen. Measuring the
   * sheet cannot be fooled that way.
   */
  const centreHorizontally = useCallback(
    (smooth = false) => {
      const viewport = scrollViewportRef.current;
      const pages = pagesRef.current;
      if (!viewport) return;

      const left = pages
        ? Math.max(0, pages.offsetLeft + pages.offsetWidth / 2 - viewport.clientWidth / 2)
        : 0;

      // Instant when triggered by a resize — the keyboard opening should not
      // look like the page is sliding around on its own.
      if (smooth && !prefersReducedMotion()) viewport.scrollTo({ left, behavior: 'smooth' });
      else viewport.scrollLeft = left;
    },
    []
  );

  const handleFitToView = () => {
    const viewport = scrollViewportRef.current;
    setZoomIndex(FIT_TO_VIEW_INDEX);

    if (!viewport) {
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        viewport.scrollTop = 0;
        centreHorizontally();
      });
    });
  };


  /*
   * Re-centre whenever the fit scale changes.
   *
   * The fit scale is recomputed by a ResizeObserver, and on mobile the viewport
   * resizes for reasons that have nothing to do with the user — the virtual
   * keyboard, the URL bar collapsing. Without this the horizontal scroll left
   * over from a previous size survives the resize, which is what leaves the
   * sheet clipped against the left edge.
   */
  useEffect(() => {
    if (zoomIndex !== FIT_TO_VIEW_INDEX) return;
    const id = requestAnimationFrame(() => centreHorizontally());
    return () => cancelAnimationFrame(id);
  }, [fitScale, zoomIndex, centreHorizontally]);

  useEffect(() => {
    if (!previewHandleRef) return;
    previewHandleRef.current = {
      focusField: (element: HTMLElement) => {
        const viewport = scrollViewportRef.current;
        if (!viewport) return false;

        /*
         * Zoom on the basis of how small the text currently is, NOT on pointer
         * type. Gating this on `(pointer: coarse)` meant it silently did
         * nothing in a narrow desktop window — and "too small to edit" is the
         * actual condition, whatever is doing the pointing.
         */
        const targetIndex = ZOOM_LEVELS.indexOf(1);
        const nextIndex = targetIndex >= 0 ? targetIndex : ZOOM_LEVELS.length - 1;
        const readableScale = ZOOM_LEVELS[nextIndex];
        const currentScale = getScaleForZoomIndex(zoomIndex);
        const needsZoom = currentScale < readableScale - 0.01;

        // Already readable: centre the field, but leave the zoom alone.
        const targetScale = needsZoom ? readableScale : currentScale;
        if (needsZoom) setZoomIndex(nextIndex);

        /*
         * Wait for layout to actually reflect the new scale before measuring.
         *
         * A fixed number of frames is not enough: re-laying out the scaled page
         * and its iframe can take an extra frame, and measuring early yields the
         * PREVIOUS scale — so the scroll target is computed from the wrong
         * geometry and overshoots, leaving the field clipped against an edge.
         * Poll until the rendered scale matches, then measure.
         */
        let frames = 0;
        const settle = () => {
          const frame = element.ownerDocument.defaultView?.frameElement as HTMLElement | null;
          if (!frame) return;

          const frameRect = frame.getBoundingClientRect();
          const rendered = frameRect.width / Math.max(frame.offsetWidth, 1);

          // 40 frames ≈ 660ms, comfortably longer than ZOOM_TRANSITION_MS: the
          // rect reports intermediate values while the transform eases, so the
          // poll now has to outlast the animation rather than a single reflow.
          if (needsZoom && Math.abs(rendered - targetScale) > 0.01 && frames < 40) {
            frames += 1;
            requestAnimationFrame(settle);
            return;
          }

          const elementRect = element.getBoundingClientRect();
          const viewportRect = viewport.getBoundingClientRect();

          const elementCentreX = frameRect.left + (elementRect.left + elementRect.width / 2) * rendered;
          const elementCentreY = frameRect.top + (elementRect.top + elementRect.height / 2) * rendered;

          viewport.scrollTo({
            left:
              viewport.scrollLeft + elementCentreX - (viewportRect.left + viewport.clientWidth / 2),
            // Sit the field above centre: the virtual keyboard covers the lower
            // half, so a true centre would hide what is being typed.
            top:
              viewport.scrollTop +
              elementCentreY -
              (viewportRect.top + viewport.clientHeight * 0.32),
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          });
        };

        requestAnimationFrame(settle);
        return needsZoom;
      },
      fitToView: () => {
        setZoomIndex(FIT_TO_VIEW_INDEX);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const viewport = scrollViewportRef.current;
            if (viewport) {
              viewport.scrollTo({
                top: 0,
                behavior: prefersReducedMotion() ? 'auto' : 'smooth',
              });
            }
            centreHorizontally(true);
          });
        });
      },
    };
  }, [previewHandleRef, centreHorizontally, getScaleForZoomIndex, zoomIndex]);

  if (!html) return null;

  const scale = currentZoom === 'fit' ? fitScale : currentZoom;
  const isFitToView = currentZoom === 'fit';
  const displayedPageOffsets = measuredHtml === printHtml ? pageOffsets : [0];
  const displayedVisibleHeights = displayedPageOffsets.map((offsetPx, index) => {
    const nextOffset = displayedPageOffsets[index + 1];

    if (typeof nextOffset === 'number') {
      return Math.max(MIN_PAGE_WINDOW_PX, Math.min(usableHeightPx, nextOffset - offsetPx));
    }

    const remainingHeight = contentHeightPx - offsetPx;
    return Math.max(MIN_PAGE_WINDOW_PX, Math.min(usableHeightPx, remainingHeight));
  });

  return (
    <div ref={containerRef} className="relative flex h-full min-h-0 w-full min-w-0 flex-col">
      <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden">
        <div
          ref={scrollViewportRef}
          className="absolute inset-0 overflow-auto overscroll-contain"
          style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y' }}
        >
          {/*
            The measurement frame must keep its real page dimensions to lay the
            content out, but it must NOT enlarge the scroll area: it is a
            direct, absolutely positioned child of the scroll container, so its
            fixed ~642px width counted towards scrollWidth. On a phone that made
            scrollWidth far wider than the viewport, and centring on that phantom
            overflow pushed the (correctly fit-scaled) sheet off the left edge.

            A zero-size clipping wrapper keeps the layout and drops the overflow.
          */}
          <div
            aria-hidden
            className="pointer-events-none absolute left-0 top-0 h-0 w-0 overflow-hidden"
          >
            <iframe
              ref={measureIframeRef}
              srcDoc={printHtml}
              title="Resume Preview Measurement"
              className="pointer-events-none opacity-0"
              style={{ width: `${pdfContentWidthMm}mm`, height: `${pdfContentHeightMm}mm` }}
              onLoad={handleMeasureLoad}
            />
          </div>

          <div className="min-h-full w-max min-w-full p-4 pb-24">
            <div
              ref={pagesRef}
              className="mx-auto flex w-max flex-col items-center"
              style={{ gap: `${PREVIEW_PAGE_GAP_PX}px` }}
            >
              {displayedPageOffsets.map((offsetPx, pageIndex) => (
                <PreviewPage
                  key={`resume-preview-page-${pageIndex}-${Math.round(offsetPx)}-${printHtml.length}`}
                  html={printHtml}
                  offsetPx={offsetPx}
                  visibleHeightPx={displayedVisibleHeights[pageIndex]}
                  pageNumber={pageIndex + 1}
                  scale={scale}
                  animateScale={!reduceMotion}
                  contentHeightPx={contentHeightPx}
                  pdfMarginMm={pdfMarginMm}
                  pdfContentWidthMm={pdfContentWidthMm}
                  pageInsetYMm={pageInsetYPx / MM_TO_PX}
                  canvasBackground={canvasBackground}
                  onRequestEdit={onRequestEdit}
                  onRequestDateEdit={onRequestDateEdit}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div
        className={
          controlsFixedToViewport
            ? 'fixed bottom-4 left-1/2 z-[70] -translate-x-1/2'
            : 'pointer-events-none absolute bottom-4 left-1/2 z-20 -translate-x-1/2'
        }
      >
        <div className="pointer-events-auto flex items-center gap-1 rounded-lg border border-border bg-background px-2 py-1.5 shadow-lg">
          {editing && onSaveEdit ? (
            <>
              {/* Editing already saves on blur, but on touch "tap somewhere
                  else" is not a discoverable save. An explicit control also
                  gives a way back to Fit without hunting for the zoom bar. */}
              <Button variant="ghost" size="sm" onClick={onCancelEdit} className="h-7 px-2">
                Cancel
              </Button>
              <Button size="sm" onClick={onSaveEdit} className="h-7 gap-1 px-3">
                <Check className="h-4 w-4" />
                Save
              </Button>
            </>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomOut}
            disabled={!canZoomOut}
            className="h-7 w-7 p-0"
            title="Zoom out"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-2 px-2">
            <button
              type="button"
              onClick={handleFitToView}
              className="min-w-[60px] text-sm font-medium hover:text-primary transition-colors"
              title="Click to fit to view"
            >
              {isFitToView ? 'Fit' : `${Math.round((scale as number) * 100)}%`}
            </button>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomIn}
            disabled={!canZoomIn}
            className="h-7 w-7 p-0"
            title="Zoom in"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>

          <div className="mx-1 h-5 w-px bg-border" />

          <Button
            variant="ghost"
            size="sm"
            onClick={handleFitToView}
            className="h-7 w-7 p-0"
            title="Fit to view"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
